// src/Components/charts/chartCard.jsx
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

const COLORS = {
  worksheets: "#075B4A",
  practices: "#F59E0B",
  teachers: "#3B82F6",
  registrations: "#8B5CF6",
  discounts: "#EC4899",
};

// ==================================================================
// 📅 محاسبه اولین روز هفته (دوشنبه) از ISO week
// ==================================================================
function getMondayOfISOWeek(year, week) {
  // ISO: هفته ۱ همیشه شامل ۴ ژانویه هست
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Day = jan4.getUTCDay() || 7; // 1=دوشنبه ... 7=یکشنبه

  // دوشنبه‌ی هفته ۱
  const week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - (jan4Day - 1));

  // اضافه کردن هفته‌ها
  const monday = new Date(week1Monday);
  monday.setUTCDate(week1Monday.getUTCDate() + (week - 1) * 7);

  return monday;
}

// ==================================================================
// 📅 فرمت‌دهنده‌ها
// ==================================================================

// «۲ شهریور»
function formatDayShort(raw) {
  const d = new Date(raw);
  if (isNaN(d.getTime())) return String(raw);
  return d.toLocaleDateString("fa-IR", { month: "short", day: "numeric" });
}

// «۲ شهریور» — اولین روز هفته
function formatWeekStart(year, week) {
  const monday = getMondayOfISOWeek(year, week);
  return monday.toLocaleDateString("fa-IR", {
    month: "short",
    day: "numeric",
  });
}

// «شهریور» — اسم ماه
function formatMonthName(year, month) {
  const d = new Date(Date.UTC(year, month - 1, 1));
  return d.toLocaleDateString("fa-IR", { month: "long" });
}

// ==================================================================
// 🔄 تبدیل هر آیتم به { label, value, sortKey }
// ==================================================================
function toItem(item, index) {
  const value = item.count ?? item.value ?? item.uniqueTeachers ?? 0;

  // ۱) year + week → تاریخ اولین روز هفته
  if (item.year && item.week !== undefined) {
    const sortKey = item.year * 100 + item.week;
    return {
      label: formatWeekStart(item.year, item.week),
      value,
      sortKey,
    };
  }

  // ۲) year + month → اسم ماه
  if (item.year && item.month !== undefined) {
    const sortKey = item.year * 100 + item.month;
    return {
      label: formatMonthName(item.year, item.month),
      value,
      sortKey,
    };
  }

  // ۳) date → تاریخ روز
  if (item.date) {
    const d = new Date(item.date);
    const sortKey = isNaN(d.getTime()) ? 0 : d.getTime();
    return { label: formatDayShort(item.date), value, sortKey };
  }

  return { label: `${index + 1}`, value, sortKey: index };
}

export function normalizeSeries(input) {
  if (!input) return [];

  if (Array.isArray(input) && typeof input[0] === "number") {
    return input.map((v, i) => ({ label: `${i + 1}`, value: v, sortKey: i }));
  }

  if (Array.isArray(input) && typeof input[0] === "object") {
    return input.map((item, i) => toItem(item, i));
  }

  if (typeof input === "object") {
    return Object.entries(input).map(([k, v]) => ({
      label: formatDayShort(k),
      value: typeof v === "number" ? v : (v?.count ?? 0),
      sortKey: new Date(k).getTime() || 0,
    }));
  }

  return [];
}

// ==================================================================
// 🎨 کامپوننت اصلی
// ==================================================================
export default function ChartCard({
  title,
  type = "line",
  series = [],
  height = 280,
  big = false,
}) {
  // نرمال‌سازی
  const normalized = series
    .map((s) => ({
      key: s.key,
      label: s.label,
      data: normalizeSeries(s.data),
    }))
    .filter((s) => s.data.length > 0);

  // جمع‌آوری labelها + sortKey برای مرتب‌سازی زمانی
  const labelSortKeys = new Map();
  normalized.forEach((s) => {
    s.data.forEach((d) => {
      if (!labelSortKeys.has(d.label)) {
        labelSortKeys.set(d.label, d.sortKey ?? 0);
      }
    });
  });

  // مرتب‌سازی: قدیمی → جدید
  const labels = [...labelSortKeys.keys()].sort(
    (a, b) => (labelSortKeys.get(a) ?? 0) - (labelSortKeys.get(b) ?? 0),
  );

  const chartData = labels.map((label) => {
    const row = { label };
    normalized.forEach((s) => {
      const found = s.data.find((d) => d.label === label);
      row[s.key] = found?.value ?? 0;
    });
    return row;
  });

  const hasData = chartData.length > 0;
  const ChartComp = type === "bar" ? BarChart : LineChart;

  return (
    <div className={`bg-gray-50 rounded-lg ${big ? "p-6" : "p-4"}`}>
      <h3
        className={`font-bold text-gray-700 mb-4 ${
          big ? "text-lg" : "text-sm"
        }`}
      >
        {title}
      </h3>

      {!hasData ? (
        <div
          className="text-center text-gray-400 text-sm flex items-center justify-center"
          style={{ height }}
        >
          داده‌ای برای نمایش نیست
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          <ChartComp
            data={chartData}
            margin={{ top: 10, right: 15, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="label" tick={{ fontSize: big ? 14 : 12 }} />
            <YAxis tick={{ fontSize: big ? 14 : 12 }} allowDecimals={false} />
            <Tooltip
              contentStyle={{
                fontSize: big ? 15 : 13,
                direction: "rtl",
              }}
              formatter={(v, name) => [`${v}`, name]}
            />
            <Legend wrapperStyle={{ fontSize: big ? 15 : 13 }} />

            {normalized.map((s) => {
              const color = COLORS[s.key] ?? "#888";

              return type === "bar" ? (
                <Bar
                  key={s.key}
                  dataKey={s.key}
                  name={s.label}
                  fill={color}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={big ? 60 : 40}
                />
              ) : (
                <Line
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={s.label}
                  stroke={color}
                  strokeWidth={big ? 3 : 2.5}
                  dot={{
                    r: big ? 7 : 5,
                    fill: color,
                    stroke: "#fff",
                    strokeWidth: 2,
                  }}
                  activeDot={{ r: big ? 10 : 7 }}
                />
              );
            })}
          </ChartComp>
        </ResponsiveContainer>
      )}
    </div>
  );
}
